import { t } from 'i18next'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import EmploymentForm from '../../components/Form/EmploymentForm'
import navigation from '../../config/navigation'
import { useNotification } from '../../store/redux/hooks'
import { useAddContractMutation } from '../../store/redux/reducers/contractSlice'
import { getCurrentUser, getIsManagementOrAdministrator } from '../../store/redux/reducers/currentUserSlice'
import { useGetDictionariesQuery } from '../../store/redux/reducers/dictionarySlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { useGetUserByIdQuery } from '../../store/redux/reducers/usersSlice'
import { RootState } from '../../store/redux/store'
import { EmploymentFormValues } from '../../store/types/form/EmploymentFormValues'
import { createErrorMessage } from '../../store/utils'
import { mapFormVacationPeriodsToModifyDTO } from '../../store/utils/employmentUtils'

const AddContractView: React.FC = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { displayNotification } = useNotification()
  const currentUser = useSelector(getCurrentUser)
  const isManagementOrAdministrator = useSelector((state: RootState) => getIsManagementOrAdministrator(state))
  const employeeId = userId ?? currentUser?.id

  const [addContract] = useAddContractMutation()

  const { data: options } = useGetDictionariesQuery({
    page: 0,
    size: 20,
    group: 'EMPLOYMENT_TYPE',
  })

  const handleClose = () => {
    if (userId) {
      navigate(generatePath(navigation.user, { userId }))
    } else {
      navigate(navigation.users)
    }
  }

  const handleSubmit = async (values: EmploymentFormValues, allowNavigation: () => void) => {
    if (!isManagementOrAdministrator) return
    if (!employeeId) return
    if (!options) return

    const contractTypeId = options.content.find(v => v.key === values.employmentType)?.id

    if (!contractTypeId) return

    const mappedPeriods = mapFormVacationPeriodsToModifyDTO(values.vacationPeriods)

    try {
      const payload = {
        employmentPercentage: Number(values.employmentPercentage) || 0,
        paidVacationPeriods: mappedPeriods,
        paidVacation: values.paidVacation,
        version: null,
        startDate: values.startDate ? values.startDate.utc(true).toISOString() : '',
        indefiniteDuration: values.indefiniteDuration,
        endDate: values.indefiniteDuration ? null : values.endDate ? values.endDate.utc(true).toISOString() : null,
        contractTypeId: contractTypeId,
        employeeId: employeeId,
        settlementMethod: values.settlementMethod,
      }

      await addContract(payload).unwrap()

      displayNotification({
        message: t('page.employments.notifications.addEmploymentSuccess'),
        type: 'success',
      })

      allowNavigation()
      handleClose()
    } catch (e) {
      const error = createErrorMessage(e)

      displayNotification({
        message: error || t('message.somethingWentWrong'),
        type: 'error',
      })
    }
  }

  useEffect(() => {
    dispatch(setPageName(t('page.name.addContract')))
    dispatch(setPageIndex(PageIndex.USERS))
  }, [dispatch])

  const { data: user } = useGetUserByIdQuery(userId as string, { skip: !userId })
  const employeeName = user ? `${user.firstName} ${user.lastName}` : undefined

  return (
    <EmploymentForm onClose={handleClose} onSubmit={handleSubmit} isNewEmployment={true} employeeName={employeeName} />
  )
}

export default AddContractView
